import { lazy, Suspense, useEffect } from "react";
import { createBrowserRouter, RouterProvider, Routes, Route, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import AuthLayout from "@components/Layout/AuthLayout";
import PublicLayout from "@components/Layout/PublicLayout";
import AuthenticatedRoutes from "@components/Routes/AuthenticatedRoutes";
import UnauthenticatedRoutes from "@components/Routes/UnauthenticatedRoutes";
import ModalContainer from "@components/ModalContainer/ModalContainer";
import { initSocket, disconnectSocket } from "@api/socket";
import { useAxiosPrivate } from "@hooks/useAxiosPrivate";
import "react-toastify/dist/ReactToastify.css";

const SharedResultsPage = lazy(() => import("@components/SharedResults/SharedResultsPage"));

/** Ensures axios auth header is set before any child (e.g. RTK Query) runs. */
function AuthAxiosSetup() {
  useAxiosPrivate({ proactive: true });
  return null;
}

/** Inner app — needs useLocation which requires Router context */
function AppInner() {
  const access_token = useSelector((state) => state.user.access_token);
  const location = useLocation();
  const isSharedResultsPage = location.pathname.startsWith("/results/");

  useEffect(() => {
    if (access_token) {
      initSocket(access_token);
    } else {
      disconnectSocket();
    }
    return () => disconnectSocket();
  }, [access_token]);

  if (isSharedResultsPage) {
    return (
      <Suspense fallback={null}>
        <Routes>
          <Route path="/results/:shareToken" element={<SharedResultsPage />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <>
      {access_token ? (
        <>
          <AuthAxiosSetup />
          <AuthLayout>
            <Routes>
              <Route path="/*" element={<AuthenticatedRoutes />} />
            </Routes>
          </AuthLayout>
        </>
      ) : (
        <PublicLayout>
          <Routes>
            <Route path="/*" element={<UnauthenticatedRoutes />} />
          </Routes>
        </PublicLayout>
      )}
      <ToastContainer
        style={{ zIndex: 10005 }}
        position="top-right"
        autoClose={3000}
        theme="dark"
      />
      <ModalContainer />
    </>
  );
}

/**
 * App Component
 *
 * Main application component. Provides the router and layout;
 * renders authenticated or unauthenticated routes based on auth state.
 * /results/:shareToken is a standalone public page (no auth layout).
 */
// A data router enables draft protection across links and browser history.
const router = createBrowserRouter(
  [{ path: "*", element: <div className="App"><AppInner /></div> }],
  { future: { v7_relativeSplatPath: true } },
);

function App() {
  return <RouterProvider router={router} future={{ v7_startTransition: true }} />;
}

export default App;

